<script setup lang="ts">
import dayjs from 'dayjs'
import { Loader2, TriangleAlert } from 'lucide-vue-next'

import useNotification from '@/composables/notification'

import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'

import TheLoadingPage from '@/components/layout/TheLoadingPage.vue'

import useAudience from '@/composables/audiences'
import useContentScope, { useContentScopeEntry } from '@/composables/contentScope'
import useOffer from '@/composables/offer'
import useReward from '@/composables/reward'
import { useOfferForm } from '@/composables/useOfferForm'
import { useOfferSummary } from '@/composables/useOfferSummary'

import RewardClaimLimitBlock from '@/components/rewards/RewardClaimLimitBlock.vue'
import OfferChannelsSection from './components/OfferChannelsSection.vue'
import OfferFormSection from './components/OfferFormSection.vue'
import OfferPromotionalCodeSection from './components/OfferPromotionalCodeSection.vue'
import OfferRewardModal from './components/OfferRewardModal.vue'
import OfferRewardSelectionModal from './components/OfferRewardSelectionModal.vue'
import OfferScheduleSection from './components/OfferScheduleSection.vue'
import OfferStoresSection from './components/OfferStoresSection.vue'
import OfferSummaryAside from './components/OfferSummaryAside.vue'
import OfferTargetSection from './components/OfferTargetSection.vue'

import { buildAvailabilitySlotsFromTimeSlots, mapDistributionChannelToApi, toIsoDate } from '@/utils/offerHelpers'

import type { LoyaltyReward, OfferCreatePayload, OfferUpdatePayload } from '@/types/types'
import { getApiErrorMessage } from '@/utils/apiError.js'

const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const notification = useNotification()
const { createOffer, fetchOffer, updateOffer } = useOffer()
const { fetchMerchantAudiences } = useAudience()
const { fetchAllRewards } = useReward()
const { canChooseScope } = useContentScope()

const isEditMode = computed(() => route.name === 'OfferEdit')

const offerId = computed(() => {
  const raw = route.params.offerId
  if (!raw) return null
  const value = Array.isArray(raw) ? Number(raw[0]) : Number(raw)
  return Number.isNaN(value) ? null : value
})

const {
  scope,
  offerName,
  internalDescription,
  selectedRewardId,
  rewards,
  isSubmitting,
  isNavigatingAfterSave,
  allCustomerLists,
  targetAudience,
  selectedSegmentIds,
  excludedSegmentIds,
  scheduleStartDate,
  scheduleEndDate,
  hasScheduleError,
  hasTargetError,
  timeSlots,
  selectedChannels,
  offerStoreIds,
  scopedStoreIds,
  claimLimitQuantity,
  claimLimitDays,
  isPromotionalCodeRequired,
  selectedReward,
  submitDisabled,
  isDirty,
  initialSnapshot,
  getCurrentSnapshot,
  hydrateFromOffer
} = useOfferForm({ isEditMode })

const { offerAudienceSummary, offerScheduleSummary, offerStatus, offerChannelsSummary, offerStoresSummary } =
  useOfferSummary({
    targetAudience,
    selectedSegmentIds,
    allCustomerLists,
    scheduleStartDate,
    scheduleEndDate,
    timeSlots,
    selectedChannels,
    offerStoreIds
  })

const isLoaded = ref(false)
const isOfferRewardModalOpen = ref(false)
const isOfferRewardSelectionModalOpen = ref(false)
const isUnsavedChangesModalOpen = ref(false)
const pendingNavigation = ref<string | null>(null)

const fetchRewards = async () => {
  try {
    const data = await fetchAllRewards()
    rewards.value = data || []
  } catch {
    rewards.value = []
  }
}

const handleOfferRewardUpdated = async (reward: LoyaltyReward) => {
  selectedRewardId.value = reward.id ?? selectedRewardId.value
  await fetchRewards()
  isOfferRewardModalOpen.value = false
}

const handleRewardSelectedFromSelectionModal = (rewardId: number) => {
  selectedRewardId.value = rewardId
}

const fetchAllCustomerLists = async () => {
  try {
    const response = await fetchMerchantAudiences({
      pagination: { pageSize: 1000, page: 1 }
    })
    allCustomerLists.value = response?.data ?? []
  } catch {
    allCustomerLists.value = []
  }
}

const { isScopeModalOpen, confirmScope, askScope } = useContentScopeEntry(scope)

const leaveFromScope = () => {
  isNavigatingAfterSave.value = true
  router.push({ name: 'OfferList' })
}

const handleCancel = () => {
  if (isEditMode.value && offerId.value != null) {
    router.push({ name: 'OfferDetail', params: { offerId: offerId.value } })
    return
  }

  router.push({ name: 'OfferList' })
}

const handleSubmit = async () => {
  if (submitDisabled.value || selectedRewardId.value === null) {
    return
  }
  isSubmitting.value = true

  try {
    if (!scheduleStartDate.value) {
      throw new Error('Missing scheduleStartDate')
    }

    const availabilitySlots = buildAvailabilitySlotsFromTimeSlots(timeSlots.value)
    const channels =
      selectedChannels.value.length > 0 ? selectedChannels.value.map((c) => mapDistributionChannelToApi(c)) : undefined

    const loyaltyProgramMemberOnly = targetAudience.value !== 'all'

    const includedSegmentIds =
      targetAudience.value === 'segment' && selectedSegmentIds.value.length > 0
        ? [...selectedSegmentIds.value]
        : undefined

    const excludedIds =
      isEditMode.value || excludedSegmentIds.value.length > 0 ? [...excludedSegmentIds.value] : undefined

    const startDateIso = toIsoDate(scheduleStartDate.value, false)
    const endDateIso = scheduleEndDate.value ? toIsoDate(scheduleEndDate.value, true) : null

    const storeIds =
      !isEditMode.value ||
      JSON.stringify([...offerStoreIds.value].sort()) !== JSON.stringify(initialSnapshot.value?.storeIds ?? [])
        ? offerStoreIds.value
        : undefined

    const payload: OfferCreatePayload & OfferUpdatePayload = {
      ...(scope.value && (!isEditMode.value || canChooseScope.value) && { scope: scope.value }),
      rewardId: selectedRewardId.value,
      name: offerName.value.trim(),
      description: internalDescription.value.trim() || undefined,
      startDate: startDateIso,
      endDate: endDateIso,
      loyaltyProgramMemberOnly,
      channels,
      includedSegmentIds,
      excludedSegmentIds: excludedIds,
      storeIds,
      availabilitySlots: isEditMode.value
        ? availabilitySlots
        : availabilitySlots.length
          ? availabilitySlots
          : undefined,
      claimLimitQuantity: claimLimitQuantity.value,
      claimLimitDays: claimLimitDays.value,
      isPromotionalCodeRequired: isPromotionalCodeRequired.value
    }

    if (isEditMode.value && offerId.value != null) {
      await updateOffer(offerId.value, payload)
      notification.create('success', {
        text: t('pages.offer.create.notifications.editSuccess')
      })
      initialSnapshot.value = getCurrentSnapshot()
      isNavigatingAfterSave.value = true
      router.push({ name: 'OfferDetail', params: { offerId: offerId.value } })
    } else {
      const newOffer = await createOffer(payload)
      notification.create('success', {
        text: t('pages.offer.create.notifications.success')
      })
      isNavigatingAfterSave.value = true
      router.push({ name: 'OfferDetail', params: { offerId: newOffer.id } })
    }
  } catch (error: unknown) {
    notification.create('error', {
      text:
        getApiErrorMessage(error) ??
        (isEditMode.value
          ? t('pages.offer.create.notifications.editError')
          : t('pages.offer.create.notifications.error'))
    })
  } finally {
    isSubmitting.value = false
  }
}

onBeforeRouteLeave((to) => {
  if (isNavigatingAfterSave.value || !isDirty.value || isSubmitting.value) {
    return true
  }
  pendingNavigation.value = to.fullPath
  isUnsavedChangesModalOpen.value = true
  return false
})

const confirmLeave = () => {
  isUnsavedChangesModalOpen.value = false
  isNavigatingAfterSave.value = true
  if (pendingNavigation.value) {
    router.push(pendingNavigation.value)
  }
}

const cancelLeave = () => {
  isUnsavedChangesModalOpen.value = false
  pendingNavigation.value = null
}

onMounted(async () => {
  const tasks: Promise<unknown>[] = [fetchRewards(), fetchAllCustomerLists()]

  if (isEditMode.value && offerId.value != null) {
    tasks.push(
      (async () => {
        const data = await fetchOffer(offerId.value as number)
        hydrateFromOffer(data)
      })()
    )
  } else {
    selectedChannels.value = ['in_store', 'kiosk', 'click_collect', 'online', 'delivery']
  }

  await Promise.all(tasks)

  if (!isEditMode.value && scheduleStartDate.value == null) {
    scheduleStartDate.value = dayjs().format('YYYY-MM-DD')
  }

  isLoaded.value = true

  if (!isEditMode.value) askScope()
})
</script>

<template>
  <TheLoadingPage v-if="!isLoaded" />

  <template v-else>
    <div class="min-h-screen bg-muted/60">
      <div v-if="isLoaded" class="mx-auto max-w-6xl pb-12">
        <TheDetailHeader
          :title="isEditMode ? t('pages.offer.create.editTitle') : t('pages.offer.create.title')"
          :parent="{ label: t('pages.offer.create.crumbs.promotions'), to: { name: 'OfferList' } }"
        >
          <template #actions>
            <Button variant="outline" @click="handleCancel">
              {{ t('pages.offer.create.buttons.cancel') }}
            </Button>
            <div class="flex flex-col items-end gap-1">
              <Button
                variant="default-primary"
                :disabled="submitDisabled || (isEditMode && !isDirty) || isSubmitting"
                @click="handleSubmit"
              >
                <Loader2 v-if="isSubmitting" class="animate-spin" />
                {{ isEditMode ? t('pages.offer.create.buttons.save') : t('pages.offer.create.buttons.create') }}
              </Button>
            </div>
          </template>
        </TheDetailHeader>

        <div class="px-6 sm:px-12">
          <div class="grid gap-6 lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)] lg:items-start">
            <div class="space-y-5">
              <OfferFormSection
                v-model:offer-name="offerName"
                v-model:internal-description="internalDescription"
                v-model:selected-reward-id="selectedRewardId"
                :selected-reward="selectedReward"
                :rewards="rewards"
                @open-offer-reward-modal="isOfferRewardModalOpen = true"
                @open-offer-reward-selection-modal="isOfferRewardSelectionModalOpen = true"
              />

              <OfferStoresSection
                v-model:store-ids="offerStoreIds"
                :selected-reward="selectedReward"
                :scoped-store-ids="scopedStoreIds"
              />

              <OfferTargetSection
                v-model:target-audience="targetAudience"
                v-model:selected-segment-ids="selectedSegmentIds"
                :all-customer-lists="allCustomerLists"
                @has-error="hasTargetError = $event"
              />

              <OfferScheduleSection
                v-model:schedule-start-date="scheduleStartDate"
                v-model:schedule-end-date="scheduleEndDate"
                v-model:time-slots="timeSlots"
                :is-edit-mode="isEditMode"
                @has-error="hasScheduleError = $event"
              />

              <RewardClaimLimitBlock
                v-if="targetAudience !== 'all'"
                v-model:claim-limit-quantity="claimLimitQuantity"
                v-model:claim-limit-days="claimLimitDays"
              />

              <OfferChannelsSection v-model:selected-channels="selectedChannels" />

              <OfferPromotionalCodeSection v-model:is-promotional-code-required="isPromotionalCodeRequired" />
            </div>

            <div class="lg:sticky lg:top-24 lg:self-start">
              <OfferSummaryAside
                :offer-name="offerName"
                :selected-reward="selectedReward"
                :claim-limit-quantity="claimLimitQuantity"
                :claim-limit-days="claimLimitDays"
                :audience-summary="offerAudienceSummary"
                :schedule-summary="offerScheduleSummary"
                :channels-summary="offerChannelsSummary"
                :stores-summary="offerStoresSummary"
                :is-promotional-code-required="isPromotionalCodeRequired"
                :status="isEditMode ? offerStatus : undefined"
              />
            </div>
          </div>
        </div>
      </div>

      <OfferRewardModal
        v-if="isOfferRewardModalOpen"
        :reward-id="selectedRewardId ?? undefined"
        @close="isOfferRewardModalOpen = false"
        @update:reward="handleOfferRewardUpdated"
      />

      <OfferRewardSelectionModal
        v-if="isOfferRewardSelectionModalOpen"
        v-model:selected-reward-id="selectedRewardId"
        v-model:is-selection-modal-open="isOfferRewardSelectionModalOpen"
        v-model:is-create-edit-reward-modal-open="isOfferRewardModalOpen"
        :rewards="rewards"
        @select-reward="handleRewardSelectedFromSelectionModal"
        @update:reward="handleOfferRewardUpdated"
      />

      <BaseModal :is-open="isUnsavedChangesModalOpen" size="lg" :show-footer="false" @close="cancelLeave">
        <div class="flex w-full flex-row justify-center">
          <div class="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
            <TriangleAlert class="h-6 w-6 text-amber-600" />
          </div>
        </div>
        <p class="mt-5 text-center font-semibold">
          {{ t('pages.offer.create.unsavedChangesModal.title') }}
        </p>
        <p class="mt-2 text-center text-sm text-foreground-secondary">
          {{ t('pages.offer.create.unsavedChangesModal.body') }}
        </p>
        <div class="mt-6 flex w-full flex-row justify-center gap-3">
          <Button variant="outline" class="flex-1" @click="cancelLeave">
            {{ t('global.words.cancel') }}
          </Button>
          <Button variant="default-primary" class="flex-1" @click="confirmLeave">
            {{ t('pages.offer.create.unsavedChangesModal.confirm') }}
          </Button>
        </div>
      </BaseModal>

      <ContentScopeModal
        v-model:open="isScopeModalOpen"
        subject="offer"
        @confirm="confirmScope"
        @cancel="leaveFromScope"
      />
    </div>
  </template>
</template>
